import React from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '../types/Product';
import ProductCard from './ProductCard';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface ProductGridProps {
  title: string;
  products: Product[];
  onAddToCart?: (product: Product) => void;
}

const ProductGrid: React.FC<ProductGridProps> = ({ title, products, onAddToCart }) => {
  const isFirstSection = title === "Featured Collection";
  const { elementRef: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.3 });
  
  return (
    <section 
      className="py-16 sm:py-20 lg:py-24 bg-white"
      data-section={isFirstSection ? "products" : undefined}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-12 lg:mb-16 transition-all duration-700 ${
            headerVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
            {title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-accent-600 to-accent-500 mx-auto rounded-full"></div>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-16 animate-fade-in-up">
            <div className="w-32 h-32 mx-auto mb-6 bg-neutral-100 rounded-full flex items-center justify-center animate-float">
              <div className="text-4xl animate-bounce-soft">🎆</div>
            </div>
            <h3 className="text-2xl font-semibold text-neutral-700 mb-3 animate-fade-in-up">No products available</h3>
            <p className="text-neutral-500 max-w-md mx-auto animate-fade-in-up">
              We're working hard to bring you amazing fireworks. Check back soon for exciting new products!
            </p>
          </div>
        ) : (
          <>
            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8 mb-12">
              {products.map((product, index) => (
                <div 
                  key={product.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <ProductCard 
                    product={product} 
                    onAddToCart={onAddToCart}
                  />
                </div>
              ))}
            </div>

            {/* View All Button */}
            {products.length >= 4 && (
              <div className="text-center">
                <Link 
                  to="/products"
                  className="bg-neutral-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-neutral-800 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-neutral-900/25 relative overflow-hidden group inline-block"
                >
                  <span className="relative z-10">View All Products</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-neutral-800 to-neutral-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ProductGrid;